import { useState } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSeeds } from '@/hooks/use-seeds';
import { cn } from '@/lib/utils';
import {
  ContentSeedLinkType,
  type ContentSeedLink,
  type ContentSeedLinkType as ContentSeedLinkTypeT,
} from '@/types/content';

// SPEC-021: SeedLinkPicker renders a searchable seeds list + a dropdown
// to attach each selected seed as prerequisite / companion / followup.
// The parent owns the links array — this component emits onChange.

interface SeedLinkPickerProps {
  links: Array<Pick<ContentSeedLink, 'seed_id' | 'link_type'>>;
  onChange: (
    links: Array<Pick<ContentSeedLink, 'seed_id' | 'link_type'>>,
  ) => void;
}

const LINK_TYPE_LABELS: Record<ContentSeedLinkTypeT, string> = {
  prerequisite: 'Prerequisite',
  companion: 'Companion',
  followup: 'Follow-up',
};

export function SeedLinkPicker({ links, onChange }: SeedLinkPickerProps) {
  const [search, setSearch] = useState('');
  const seedsQuery = useSeeds({ search: search || undefined, per_page: 10 });
  const seeds = seedsQuery.data?.seeds ?? [];
  const seedsById = new Map(seeds.map((s) => [s.id, s]));

  // We also need titles for already-linked seeds that are NOT in the
  // current search result set. Query the catalog without a search
  // filter as a fallback pool.
  const fallbackQuery = useSeeds({ per_page: 50 });
  for (const s of fallbackQuery.data?.seeds ?? []) {
    if (!seedsById.has(s.id)) seedsById.set(s.id, s);
  }

  const addSeed = (seedId: string) => {
    if (links.some((l) => l.seed_id === seedId)) return;
    onChange([...links, { seed_id: seedId, link_type: ContentSeedLinkType.Companion }]);
  };

  const removeSeed = (seedId: string) => {
    onChange(links.filter((l) => l.seed_id !== seedId));
  };

  const setLinkType = (seedId: string, linkType: ContentSeedLinkTypeT) => {
    onChange(links.map((l) => (l.seed_id === seedId ? { ...l, link_type: linkType } : l)));
  };

  return (
    <div className="space-y-3">
      <Label>Related practice seeds</Label>
      <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          placeholder="Search seeds by title..."
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
        />
      </div>
      {search && (
        <div className="max-h-56 overflow-y-auto rounded-md border">
          {seedsQuery.isLoading && (
            <p className="px-3 py-2 text-xs text-muted-foreground">Searching…</p>
          )}
          {!seedsQuery.isLoading && seeds.length === 0 && (
            <p className="px-3 py-2 text-xs text-muted-foreground">No seeds found.</p>
          )}
          {seeds.map((seed) => {
            const alreadyLinked = links.some((l) => l.seed_id === seed.id);
            return (
              <button
                type="button"
                key={seed.id}
                disabled={alreadyLinked}
                onClick={() => addSeed(seed.id)}
                className={cn(
                  'flex w-full items-start gap-2 border-b px-3 py-2 text-left text-sm last:border-b-0',
                  alreadyLinked
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:bg-accent',
                )}
              >
                <div className="flex-1">
                  <p className="font-medium">{seed.title}</p>
                  <p className="text-xs text-muted-foreground">{seed.problem_category}</p>
                </div>
                {alreadyLinked && <span className="text-xs">linked</span>}
              </button>
            );
          })}
        </div>
      )}

      {links.length > 0 && (
        <ul className="divide-y rounded-md border">
          {links.map((link) => {
            const seed = seedsById.get(link.seed_id);
            return (
              <li key={link.seed_id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">
                    {seed?.title ?? `Seed ${link.seed_id.slice(0, 8)}…`}
                  </p>
                  {seed && (
                    <p className="text-xs text-muted-foreground">{seed.problem_category}</p>
                  )}
                </div>
                <Select
                  value={link.link_type}
                  onValueChange={(v) => setLinkType(link.seed_id, v as ContentSeedLinkTypeT)}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(LINK_TYPE_LABELS) as ContentSeedLinkTypeT[]).map((k) => (
                      <SelectItem key={k} value={k}>
                        {LINK_TYPE_LABELS[k]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => removeSeed(link.seed_id)}
                  title="Unlink"
                >
                  <X className="h-4 w-4" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
